import { useApolloClient } from "@apollo/client";
import {
  type FetchResult,
  type MutationResult,
  type SetViewerInboxThreadReadStateMutation,
  useSetViewerInboxThreadReadStateMutation,
} from "@qw/graphql";
import { useCallback } from "react";
import type { MaybePartialInboxConversation } from "@/types";

export type UseSetThreadReadStateReturn = [
  (
    conversation: MaybePartialInboxConversation,
    read: boolean
  ) => Promise<FetchResult<SetViewerInboxThreadReadStateMutation> | undefined>,
  MutationResult<SetViewerInboxThreadReadStateMutation>,
];

/**
 * Set the viewer's own read/unread state on a shared thread via the per-user receipt path.
 *
 * Unlike the `UNREAD`-label hooks, this never touches `Conversation.labels` or the owner's Gmail —
 * it writes only the caller's `ConversationReadReceipt` and optimistically flips the copy's
 * `viewerIsUnread` overlay in the cache so the row updates immediately.
 */
export const useSetThreadReadState = (): UseSetThreadReadStateReturn => {
  const apolloClient = useApolloClient();
  const [setReadStateMutation, result] = useSetViewerInboxThreadReadStateMutation();

  const setThreadReadState = useCallback(
    (conversation: MaybePartialInboxConversation, read: boolean) => {
      const teamConversationId = "teamConversationId" in conversation ? conversation.teamConversationId : null;

      // A shared thread always carries its cross-inbox identity; without it the receipt has nowhere
      // to land, so there is nothing to write.
      if (!teamConversationId) {
        return Promise.resolve(undefined);
      }

      const cacheId = apolloClient.cache.identify({ __typename: "Conversation", id: conversation.id });

      return setReadStateMutation({
        variables: { input: { teamConversationId, read } },
        optimisticResponse: { setViewerInboxThreadReadState: true },
        update: (cache) => {
          if (!cacheId) {
            return;
          }

          cache.modify({
            id: cacheId,
            fields: {
              viewerIsUnread: () => !read,
            },
          });
        },
      });
    },
    [apolloClient, setReadStateMutation]
  );

  return [setThreadReadState, result];
};
